// PLATFORM-1 — reading one account's navigation configuration.
//
// THE THREE STATES, and they are the reason this is a hook of its own rather
// than a fetch inside useNavSections. "We could not read your configuration",
// "you have not configured anything" and "here is your configuration" are
// three different situations, and the first must never look like an empty
// menu: a merchant whose navigation vanished because a read failed would
// think the product broke.

import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useOptionalAuth } from '../providers/AuthProvider'
import type { AccountNavConfig, AccountNavState } from '../lib/navAccountLayer'
import { sharedRead } from '../lib/sharedRead'

/** Postgres / PostgREST for "that relation does not exist". */
const UNDEFINED_TABLE = new Set(['42P01', 'PGRST205'])

/**
 * PANEL-INTENT-1 — THE MENU CAN BE ASKED TO RE-READ ITSELF.
 *
 * This was useState + useEffect keyed only on the account, so NOTHING could
 * ask it to re-read. When Ghost renamed a menu item the merchant's menu kept
 * the old label until they reloaded the page by hand - which is exactly what
 * happened on production.
 *
 * WHY THIS IS A TINY SUBSCRIPTION AND NOT A REACT QUERY. Converting it to
 * useQuery worked and broke a promise this file makes a few lines below: a
 * sidebar rendered WITHOUT a provider must not throw, because navigation is
 * not allowed to fail for want of an optional feature. useQuery throws with no
 * QueryClient above it, and three existing render paths have none. So the
 * signal is a module-level listener set: no provider required, nothing new to
 * learn, and `refreshAccountNav()` is the whole API.
 */
const listeners = new Set<() => void>()

/** Ask every mounted menu to re-read its configuration. */
export function refreshAccountNav(): void {
  for (const l of listeners) l()
}

/**
 * THE-TAB-IS-IN-THE-MENU-1 — ANOTHER PART OF THE MENU HEARS THE SAME SIGNAL.
 *
 * The panel already calls `refreshAccountNav()` the moment a build lands.
 * Built tabs are part of the menu, so they re-read on that same call rather
 * than on a second one the panel would have to remember to make.
 */
export function onAccountNavRefresh(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function useAccountNavConfig(): AccountNavState {
  // OPTIONAL on purpose. This layer only ever subtracts, so with no auth
  // context there is nothing to subtract and the full tree is correct. A
  // sidebar rendered without a provider must not throw because of a feature
  // that can only remove things.
  const currentSubAccountId = useOptionalAuth()?.currentSubAccountId ?? null
  const [state, setState] = useState<AccountNavState>({ state: 'loading' })
  const [nonce, setNonce] = useState(0)

  // Re-read whenever anything calls refreshAccountNav().
  const bump = useCallback(() => setNonce((n) => n + 1), [])
  useEffect(() => {
    listeners.add(bump)
    return () => {
      listeners.delete(bump)
    }
  }, [bump])

  useEffect(() => {
    if (!currentSubAccountId) {
      // SHELF-FLASH-2 — UNKNOWN IS NOT NOTHING HIDDEN.
      //
      // This said `loaded` with an empty config, and an empty config subtracts
      // nothing, so a brand new account that hides five of its seven sections
      // rendered all seven — Team included — until the account resolved. The
      // owner watched it on production the night this wave was written.
      //
      // "There is no business yet" is not "this business has configured
      // nothing". It is the FIRST of the three states this file's own header
      // insists on, and reporting it as the second is what broke.
      setState({ state: 'loading' })
      return
    }
    let cancelled = false
    void (async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sb = supabase as any
      // THE-HOME-LOADS-FAST-1 — the same eight surfaces, the same one row set.
      // `nonce` is in the key so a refresh is a different read, never the one
      // already in flight that is about to answer with the old rows.
      const { data, error } = await sharedRead(
        `account-nav:${currentSubAccountId}:${nonce}`,
        async () =>
          (await sb
            .from('account_navigation')
            .select('node_id, hidden, custom_label, sort_order')
            .eq('sub_account_id', currentSubAccountId)) as {
            data: unknown
            error: { code?: string; message: string } | null
          },
      )
      if (cancelled) return

      if (error) {
        // THE TABLE MAY NOT EXIST on an unmigrated project. That is "this
        // account has configured nothing", not a failure - and it is the ONLY
        // error treated that way, because it is the only one where we know the
        // answer is genuinely empty.
        if (UNDEFINED_TABLE.has(String(error.code ?? ''))) {
          setState({ state: 'loaded', config: {} })
          return
        }
        setState({ state: 'failed', reason: error.message })
        return
      }

      const config: AccountNavConfig = {}
      for (const row of (data ?? []) as Array<{
        node_id: string
        hidden: boolean
        custom_label: string | null
        sort_order: number | null
      }>) {
        config[row.node_id] = {
          hidden: Boolean(row.hidden),
          label: row.custom_label,
          sortOrder: row.sort_order,
        }
      }
      setState({ state: 'loaded', config })
    })()
    return () => {
      cancelled = true
    }
  }, [currentSubAccountId, nonce])

  return state
}
